import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { App, Button, Dropdown, Empty, Form, Input, Select, Spin, Table, Tag, Typography } from 'antd';
import type { MenuProps, RefSelectProps, TableColumnsType } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, EyeOutlined, MinusCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../utils/feedback';
import { authService } from '../services/auth';
import { canManageProductCatalog } from '../modules/products/productCatalogAccessPolicy';
import { DIMENSION_DISPLAY_PROFILES, formatCatalogProductDimensions } from '../utils/productDimensions';
import {
  ProductCategory,
  ProductCategoryAssignedProduct,
  categoryErrorMessage,
  createProductCategoryTag,
  deleteProductCategoryTag,
  fetchProductCategoryAssignmentCandidates,
  fetchProductCategoryAssignments,
  fetchProductCategoryTag,
  replaceProductCategoryAssignments,
  updateProductCategoryTag,
} from '../modules/products/productCategoriesApi';
import { CATEGORY_COLOR_PRESETS } from '../modules/products/category/categoryColor';
import CategoryTag from '../modules/products/category/CategoryTag';
import '../components/layout/AppPage.css';
import './ProductCategoryDetail.css';

const { Text, Title } = Typography;
const { TextArea } = Input;

const LIST_PATH = '/app/products?tab=categories';

interface CategoryFormValues {
  name: string;
  description?: string | null;
  color?: string | null;
  dimensionDisplayProfile?: string | null;
}

const toFormValues = (category: ProductCategory | null): CategoryFormValues => ({
  name: category?.name ?? '',
  description: category?.description ?? undefined,
  color: category?.color ?? undefined,
  dimensionDisplayProfile: category?.dimensionDisplayProfile || 'GENERIC',
});

const sameIds = (left: string[], right: string[]) =>
  left.length === right.length && [...left].sort().join('|') === [...right].sort().join('|');

const productSearchLabel = (product: ProductCategoryAssignedProduct) => [
  product.code,
  product.name,
  product.englishName,
  product.ean,
  formatCatalogProductDimensions(product),
].filter(Boolean).join(' ');

/**
 * Category card: edit the category, pick its products and delete it on one
 * page. Replaces the edit and "assign products" modals of the Kategorie tab.
 */
const ProductCategoryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['products', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [form] = Form.useForm<CategoryFormValues>();
  const canEdit = canManageProductCatalog(authService.getCurrentUser());

  const [category, setCategory] = useState<ProductCategory | null>(null);
  const [candidates, setCandidates] = useState<ProductCategoryAssignedProduct[]>([]);
  const [savedIds, setSavedIds] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [assignedById, setAssignedById] = useState<Map<string, ProductCategoryAssignedProduct>>(new Map());
  const [formDirty, setFormDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const productsRef = useRef<HTMLElement>(null);
  const pickerRef = useRef<RefSelectProps>(null);

  const load = async () => {
    setLoading(true);
    setReady(false);
    try {
      const [loadedCategory, loadedCandidates, assigned] = await Promise.all([
        isNew ? Promise.resolve(null) : fetchProductCategoryTag(id as string),
        fetchProductCategoryAssignmentCandidates(),
        isNew ? Promise.resolve([]) : fetchProductCategoryAssignments(id as string),
      ]);
      if (!isNew && !loadedCategory) {
        setNotFound(true);
        return;
      }
      setNotFound(false);
      setCategory(loadedCategory);
      setCandidates(loadedCandidates);
      setAssignedById(new Map(assigned.map((product) => [product.id, product])));
      const ids = assigned.map((product) => product.id);
      setSavedIds(ids);
      setSelectedIds(ids);
      form.setFieldsValue(toFormValues(loadedCategory));
      setFormDirty(false);
      setReady(true);
    } catch (error) {
      feedback.error(t('products:productCategory.assignmentLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // "Przypisz produkty" in the list opens the card scrolled to the products section.
  useEffect(() => {
    if (ready && location.hash === '#products') {
      productsRef.current?.scrollIntoView({ block: 'start' });
    }
  }, [ready, location.hash]);

  const productById = useMemo(() => {
    const byId = new Map<string, ProductCategoryAssignedProduct>();
    candidates.forEach((product) => byId.set(product.id, product));
    assignedById.forEach((product, productId) => byId.set(productId, { ...byId.get(productId), ...product }));
    return byId;
  }, [candidates, assignedById]);

  const selectedProducts = useMemo(
    () => selectedIds
      .map((productId) => productById.get(productId))
      .filter((product): product is ProductCategoryAssignedProduct => Boolean(product))
      .sort((a, b) => a.name.localeCompare(b.name, 'pl')),
    [selectedIds, productById]
  );

  const addableProducts = useMemo(
    () => candidates
      .filter((product) => !selectedIds.includes(product.id))
      .sort((a, b) => a.name.localeCompare(b.name, 'pl')),
    [candidates, selectedIds]
  );

  const assignmentsDirty = !sameIds(savedIds, selectedIds);
  const dirty = formDirty || assignmentsDirty;
  const disabled = !canEdit || !ready || saving;

  const handleBack = () => {
    if (dirty && !isNew) {
      feedback.warning(t('products:productCategory.card.leaveBlocked'));
      return;
    }
    navigate(LIST_PATH);
  };

  const handleDiscard = () => {
    if (isNew) {
      navigate(LIST_PATH);
      return;
    }
    form.setFieldsValue(toFormValues(category));
    setSelectedIds(savedIds);
    setFormDirty(false);
  };

  const handleSave = async () => {
    let values: CategoryFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload = {
      name: values.name.trim(),
      description: values.description?.trim() || null,
      color: values.color || null,
      dimensionDisplayProfile: values.dimensionDisplayProfile || 'GENERIC',
    };
    setSaving(true);
    try {
      if (isNew) {
        const created = await createProductCategoryTag(payload);
        if (selectedIds.length > 0) {
          await replaceProductCategoryAssignments(created.id, selectedIds);
        }
        feedback.success(t('products:productCategory.createdSuccess', { name: payload.name }));
        navigate(`/app/products/categories/${created.id}`, { replace: true });
        return;
      }
      if (formDirty) {
        await updateProductCategoryTag(category!.id, payload);
      }
      if (assignmentsDirty) {
        await replaceProductCategoryAssignments(category!.id, selectedIds);
      }
      feedback.success(t('products:productCategory.updatedSuccess', { name: payload.name }));
      await load();
    } catch (error: any) {
      feedback.error(categoryErrorMessage(error, t, t('products:productCategory.saveFailed', { name: payload.name })));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!category) return;
    const productCount = savedIds.length;
    modal.confirm({
      title: t('products:productCategory.deleteConfirmTitle'),
      content: productCount > 0
        ? t('products:productCategory.deleteConfirmWithProducts', { count: productCount })
        : t('products:productCategory.deleteConfirm'),
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteProductCategoryTag(category.id);
          feedback.success(t('products:productCategory.deletedSuccess', { name: category.name }));
          navigate(LIST_PATH);
        } catch (error: any) {
          feedback.error(categoryErrorMessage(error, t, t('products:productCategory.deleteFailed', { name: category.name })));
        }
      },
    });
  };

  const headerMenu: MenuProps['items'] = [
    {
      key: 'delete',
      danger: true,
      icon: <DeleteOutlined />,
      label: t('products:productCategory.card.delete'),
      disabled: !canEdit || saving,
    },
  ];

  const rowMenu: MenuProps['items'] = [
    { key: 'open', icon: <EyeOutlined />, label: t('products:productCategory.card.openProduct') },
    ...(canEdit ? [{
      key: 'remove',
      danger: true,
      icon: <MinusCircleOutlined />,
      label: t('products:productCategory.card.removeProduct'),
      disabled,
    }] : []),
  ];

  const handleRowAction = (key: string, product: ProductCategoryAssignedProduct) => {
    if (key === 'open') {
      navigate(`/app/products/${product.id}`);
      return;
    }
    if (key === 'remove') {
      setSelectedIds((ids) => ids.filter((productId) => productId !== product.id));
    }
  };

  const columns: TableColumnsType<ProductCategoryAssignedProduct> = [
    {
      title: t('common:actions'),
      key: 'actions',
      width: 56,
      align: 'center',
      fixed: 'left',
      render: (_value, product) => (
        <Dropdown
          menu={{ items: rowMenu, onClick: ({ key }) => handleRowAction(key, product) }}
          trigger={['click']}
        >
          <Button
            size="small"
            icon={<MoreOutlined />}
            title={t('common:actions')}
            aria-label={t('common:actions')}
          />
        </Dropdown>
      ),
    },
    {
      title: t('products:productCategory.card.product'),
      key: 'name',
      render: (_value, product) => {
        const isPending = !savedIds.includes(product.id);
        const previousCategory = isPending && product.productCategoryId && product.productCategoryId !== category?.id
          ? product.productCategoryName
          : null;
        return (
          <div className="category-card-product">
            <button type="button" className="category-card-product__name" onClick={() => navigate(`/app/products/${product.id}`)}>
              <span>{product.name}</span>
            </button>
            {(product.englishName || product.ean) && (
              <small className="category-card-product__meta">
                {[product.englishName, product.ean].filter(Boolean).join(' · ')}
              </small>
            )}
            {isPending && (
              <Tag color="blue" className="category-card-product__pending">
                {previousCategory
                  ? t('products:productCategory.card.pendingMove', { name: previousCategory })
                  : t('products:productCategory.card.pendingAdd')}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: t('products:productCategory.card.dimensions'),
      key: 'dimensions',
      width: 280,
      render: (_value, product) => formatCatalogProductDimensions(product) || <Text type="secondary">—</Text>,
    },
  ];

  if (notFound) {
    return (
      <div className="app-page category-card-page">
        <section className="app-page__card app-page__card-body">
          <Empty description={t('products:productCategory.card.notFound')}>
            <Button onClick={() => navigate(LIST_PATH)}>{t('products:productCategory.card.back')}</Button>
          </Empty>
        </section>
      </div>
    );
  }

  const title = isNew ? t('products:productCategory.create') : (category?.name ?? '');

  return (
    <div className="app-page category-card-page">
      <Spin spinning={loading}>
        <div className="category-card-stack">
          <header className="category-card-section category-card-header">
            <div className="app-page__header-copy">
              <Button type="link" className="category-card-back" icon={<ArrowLeftOutlined />} onClick={handleBack}>
                {t('products:productCategory.card.back')}
              </Button>
              <span className="app-page__eyebrow">{t('products:productCategory.card.eyebrow')}</span>
              <Title level={2} className="category-card-title">
                {!isNew && category
                  ? <CategoryTag categoryId={category.id} name={title} color={category.color} className="category-card-title__tag" />
                  : (title || '—')}
              </Title>
            </div>
            <div className="app-page__header-aside">
              <Tag>{t('products:productCategory.card.productCount', { count: selectedIds.length })}</Tag>
              {!isNew && canEdit && (
                <Dropdown
                  menu={{ items: headerMenu, onClick: ({ key }) => key === 'delete' && handleDelete() }}
                  trigger={['click']}
                >
                  <Button icon={<MoreOutlined />} aria-label={t('common:actions')} title={t('common:actions')} />
                </Dropdown>
              )}
            </div>
          </header>

          <section className="category-card-section">
            <div className="category-card-section__header">
              <h3>{t('products:productCategory.card.detailsTitle')}</h3>
            </div>
            <Form
              form={form}
              layout="vertical"
              requiredMark={false}
              disabled={disabled}
              initialValues={toFormValues(null)}
              onValuesChange={() => setFormDirty(true)}
            >
              {/* Field widths follow content (DESIGN.md, Forms): short fields in a
                  narrow column, the description as a tall text area beside them. */}
              <div className="category-card-details">
                <Form.Item
                  name="name"
                  label={t('products:productCategory.name')}
                  rules={[{ required: true, whitespace: true, message: t('products:productCategory.nameRequired') }]}
                >
                  <Input placeholder={t('products:productCategory.namePlaceholder')} maxLength={120} />
                </Form.Item>
                <Form.Item name="color" label={t('products:productCategory.color')}>
                  <Select
                    allowClear
                    placeholder={t('products:productCategory.colorNone')}
                  >
                    {CATEGORY_COLOR_PRESETS.map((color) => (
                      <Select.Option key={color} value={color}>
                        <span className="category-card-swatch" style={{ background: color }} />
                        {t(`products:productCategory.colorNames.${color.slice(1)}`)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="dimensionDisplayProfile"
                  label={t('products:productCategory.dimensionDisplayProfile')}
                  extra={t('products:productCategory.dimensionDisplayProfileHint')}
                >
                  <Select>
                    {DIMENSION_DISPLAY_PROFILES.map((profile) => (
                      <Select.Option key={profile} value={profile}>
                        {t(`products:productCategory.dimensionProfiles.${profile}`)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="description" className="category-card-details__description" label={t('products:productCategory.description')}>
                  <TextArea rows={8} maxLength={500} showCount placeholder={t('products:productCategory.descriptionPlaceholder')} />
                </Form.Item>
              </div>
            </Form>
          </section>

          <section className="app-page__card category-card-products" id="products" ref={productsRef}>
            <div className="app-page__card-intro">
              <div className="category-card-products__copy">
                <h3>{t('products:productCategory.card.productsTitle')}</h3>
                <Text type="secondary">{t('products:productCategory.card.productsHint')}</Text>
              </div>
              {canEdit && (
                <Select
                  ref={pickerRef}
                  className="category-card-products__picker"
                  classNames={{ popup: { root: 'category-card-products__popup' } }}
                  popupMatchSelectWidth={false}
                  placement="bottomRight"
                  showSearch
                  value={null}
                  disabled={disabled}
                  placeholder={t('products:productCategory.card.addProduct')}
                  optionFilterProp="label"
                  notFoundContent={t('products:productCategory.card.noCandidates')}
                  searchValue={productSearch}
                  onSearch={setProductSearch}
                  onChange={(productId: string) => {
                    setSelectedIds((ids) => [...ids, productId]);
                    setProductSearch('');
                    pickerRef.current?.blur();
                  }}
                  options={addableProducts.map((product) => ({
                    value: product.id,
                    label: productSearchLabel(product),
                    product,
                  }))}
                  optionRender={({ data }) => {
                    const product = data.product as ProductCategoryAssignedProduct;
                    return (
                      <div className="category-card-option">
                        <span>{product.name}</span>
                        <small>
                          {[
                            formatCatalogProductDimensions(product),
                            product.productCategoryName
                              ? t('products:productCategory.currentlyInCategory', { name: product.productCategoryName })
                              : t('products:productCategory.none'),
                          ].filter(Boolean).join(' · ')}
                        </small>
                      </div>
                    );
                  }}
                />
              )}
            </div>
            <Table<ProductCategoryAssignedProduct>
              columns={columns}
              dataSource={selectedProducts}
              rowKey="id"
              size="middle"
              tableLayout="fixed"
              scroll={{ x: 760 }}
              pagination={selectedProducts.length > 25 ? { defaultPageSize: 25, showSizeChanger: true } : false}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('products:productCategory.noAssignedProducts')} /> }}
            />
          </section>
        </div>
      </Spin>

      {(dirty || isNew) && canEdit && (
        <div className="category-card-savebar" role="region" aria-label={t('common:save')}>
          <span className="category-card-savebar__status">
            {isNew ? t('products:productCategory.card.savebarNew') : t('products:productCategory.card.savebarDirty')}
          </span>
          <Button onClick={handleDiscard} disabled={saving}>{t('products:productCategory.card.discard')}</Button>
          <Button type="primary" onClick={handleSave} loading={saving} disabled={!ready} className="category-card-savebar__primary">
            {t('common:save')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProductCategoryDetail;
